import { ChangeDetectorRef, Directive, ElementRef, makeStateKey, OnDestroy, OnInit, TransferState, ViewChild } from '@angular/core';
import { getBuildIdQuery, IBrandId, IMegaMenuVisibilitySettings, ISettingsResponse } from '@ncg/data';
import { combineLatest, fromEventPattern, map, Observable, of, startWith, Subject, takeUntil } from 'rxjs';
import { FeatureDetectionService } from '../core/feature-detection.service';
import { HeaderService } from '../core/header.service';
import { ScrollStatusService } from '../core/scroll-status.service';
import { SettingsService } from '../core/settings.service';
import { STATUS } from '../navigation/mobile-menu/mobile-menu';
import { NavigationService } from '../navigation/navigation.service';
import { breakpointQueries } from '../utils/helpers/breakpoints';

@Directive() // Using directive decorator to fix DI errors.
export class BaseHeaderController implements OnInit, OnDestroy {
    private readonly unsubscribe = new Subject<void>();
    private readonly HAS_TOP_MENU_KEY = makeStateKey<boolean>('hasTopMenu');

    private previousPosition?: number;
    private currentPosition?: number;
    public headerHeight?: number;
    public isMobileMenuOpen?: boolean;
    public isMegaMenuOverlayVisible?: boolean;
    public isFixed?: boolean;
    public hideNavBar?: boolean;
    public hasTopMenu?: boolean;
    public isMc?: boolean;
    public languageLinks?: Record<string, string>;
    public settings?: ISettingsResponse;
    public buildIdQuery = getBuildIdQuery();
    public brandId?: IBrandId;

    @ViewChild('headerWrapper')
    private readonly headerWrapperElement?: ElementRef;

    @ViewChild('header')
    private readonly headerElement?: ElementRef;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly transferState: TransferState,
        private readonly headerService: HeaderService,
        private readonly scrollStatusService: ScrollStatusService,
        private readonly featureDetection: FeatureDetectionService,
        private readonly settingsService: SettingsService,
        private readonly navigationService: NavigationService,
        private readonly useFixedHeaderForMobileMenu: boolean
    ) {}

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public ngOnInit() {
        // Check for top menu.
        if (this.transferState.hasKey(this.HAS_TOP_MENU_KEY)) {
            this.hasTopMenu = this.transferState.get(this.HAS_TOP_MENU_KEY, false);
            this.cd.markForCheck();
        }

        // Check "mc" in breadcrumb == means it's an MC page. (wow... I guess this one just got hacked in. Honda only "logic").
        this.navigationService.breadcrumb$.pipe(takeUntil(this.unsubscribe)).subscribe((breadcrumb) => {
            this.isMc = (breadcrumb && breadcrumb.length > 1 && breadcrumb[1].value === '/mc') || false;
            this.cd.markForCheck();
        });

        // Set language links (nc.dk).
        this.navigationService.languageLinks$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            this.languageLinks = data;
            this.cd.markForCheck();
        });

        // Get settings and set initial values.
        // Re-trigger if changing to/from frontPage (Honda has no header on front page).
        // Re-trigger for breakpoint change (header height changes mobile&tablet to desktop).
        combineLatest([this.settingsService.get(), this.navigationService.isFrontPage(), this.isDesktop()])
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(([settings]) => {
                this.settings = settings;
                this.hasTopMenu = settings.hasTopMenu;
                this.brandId = settings.brandId;

                if (this.featureDetection.isServer()) {
                    this.transferState.set(this.HAS_TOP_MENU_KEY, this.hasTopMenu);
                }

                this.setInitialValues();
            });

        // Listen for scroll events.
        this.scrollStatusService
            .getScrollPosition()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((position: number) => {
                this.currentPosition = position;
                this.handleHeaderPosition();
            });
    }

    public onMobileStatusChange(status: STATUS) {
        this.isMobileMenuOpen = status === 0;
        if (this.isMobileMenuOpen) {
            this.handleHeaderPosition();
        }
    }

    public onMegaMenuChange(settings: IMegaMenuVisibilitySettings) {
        this.isMegaMenuOverlayVisible = settings.isVisible;
        this.cd.detectChanges();
    }

    private setInitialValues() {
        if (!this.featureDetection.isBrowser() || !this.headerElement?.nativeElement?.offsetHeight) {
            return;
        }

        setTimeout(() => {
            const headerHeight = this.headerElement?.nativeElement?.offsetHeight ?? 0;
            this.headerHeight = headerHeight;
            this.updateHeaderStyles();
            this.previousPosition = headerHeight;
            this.headerService.updateModelPageHeader(headerHeight);
            this.cd.markForCheck();
        }, 0);
    }

    private handleHeaderPosition() {
        if (this.useFixedHeaderForMobileMenu && this.isMobileMenuOpen) {
            // For sites with a fixed visible header above the (open) mobile menu.
            this.setFixedHeaderForMobileMenu();
        } else {
            this.calculateHeaderPosition();
        }
        this.cd.markForCheck();
    }

    private calculateHeaderPosition() {
        const currentPosition = this.currentPosition ?? 0;
        const previousPosition = this.previousPosition ?? 0;
        const headerHeight = this.headerHeight ?? 0;
        const hideNavBar = this.hideNavBar;

        if (currentPosition > headerHeight) {
            if (currentPosition > previousPosition) {
                this.hideNavBar = true;
                this.previousPosition = currentPosition - 1;
            } else {
                this.isFixed = true;
                this.hideNavBar = false;
                this.previousPosition = currentPosition;
            }
        } else if (currentPosition === 0) {
            this.isFixed = false;
            this.hideNavBar = false;
            this.previousPosition = headerHeight;
        }

        if (hideNavBar !== this.hideNavBar) {
            this.updateHeaderStyles();
        }
    }

    private setFixedHeaderForMobileMenu() {
        this.isFixed = true;
        this.hideNavBar = false;
    }

    private updateHeaderStyles() {
        const headerWrapper = this.headerWrapperElement?.nativeElement as HTMLElement | undefined;
        const header = this.headerElement?.nativeElement as HTMLElement | undefined;

        if (headerWrapper === undefined || header === undefined) {
            return;
        }

        headerWrapper.style.height = this.headerHeight !== undefined ? `${this.headerHeight}px` : '';
        header.style.transform = this.hideNavBar && this.headerHeight !== undefined ? `translate3d(0, ${-this.headerHeight}px, 0)` : '';
    }

    private isDesktop(): Observable<boolean> {
        if (!this.featureDetection.isBrowser()) {
            // On server, emit true as default.
            return of(true);
        }

        const mediaQuery = window.matchMedia(breakpointQueries.desktop);

        return fromEventPattern<MediaQueryListEvent>(
            (handler) => mediaQuery.addEventListener('change', handler),
            (handler) => mediaQuery.removeEventListener('change', handler)
        ).pipe(
            startWith({ matches: mediaQuery.matches } as MediaQueryListEvent), // Emit initially - do not wait for resize.
            map((event) => event.matches)
        );
    }
}
